import request from "../../utils/request";
import { Result } from "../../models/general";

export enum AssetStatus {
  Available = 'Available',
  Issued = 'Issued',
  UnderMaintenance = 'UnderMaintenance',
  Retired = 'Retired',
}

export enum AssetCondition {
  Excellent = 'Excellent',
  Good = 'Good',
  Fair = 'Fair',
  Poor = 'Poor',
}

export interface AssetView {
  id: number;
  name: string;
  categoryId: number;
  categoryName?: string;
  serialNumber?: string;
  purchasePrice?: number;
  purchaseDate?: string;
  warrantyExpiry?: string;
  condition: AssetCondition;
  status: AssetStatus;
  location?: string;
  notes?: string;
  assignedToUserId?: string;
  assignedToUserName?: string;
  createdOn: string;
}

export interface AssetCategoryView {
  id: number;
  name: string;
  description?: string;
  assetCount: number;
}

export interface AssetListView {
  items: AssetView[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AssetStatsView {
  totalAssets: number;
  available: number;
  issued: number;
  underMaintenance: number;
  retired: number;
  totalValue: number;
  awaitingFulfillment: number;
}

export interface EquipmentMatchedAssetView {
  assetName: string;
  condition: AssetCondition;
  weight: number;
}

export interface EquipmentCategoryCoverageView {
  neededItem: string;
  isCovered: boolean;
  conditionPercent?: number;
  matchedAssets: EquipmentMatchedAssetView[];
}

export interface EquipmentCoverageRowView {
  employeeId: string;
  employeeName?: string;
  department?: string;
  roleName?: string;
  coveragePercent: number;
  conditionPercent?: number;
  items: EquipmentCategoryCoverageView[];
}

export interface EquipmentCoverageView {
  rows: EquipmentCoverageRowView[];
  underCoveredCount: number;
  totalWithRequirements: number;
}

export interface AssetListQuery {
  search?: string;
  categoryId?: number;
  status?: AssetStatus;
  condition?: AssetCondition;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

export interface CreateAssetInput {
  name: string;
  categoryId: number;
  serialNumber?: string;
  purchasePrice?: number;
  purchaseDate?: string;
  warrantyExpiry?: string;
  condition: AssetCondition;
  location?: string;
  notes?: string;
}

export interface UpdateAssetInput extends CreateAssetInput {
  id: number;
}

export interface CreateAssetCategoryInput {
  name: string;
  description?: string;
}

export interface UpdateAssetCategoryInput extends CreateAssetCategoryInput {
  id: number;
}

// Assets
export async function getAssets(query: AssetListQuery): Promise<Result<AssetListView>> {
  const params = new URLSearchParams();
  if (query.search) params.append("search", query.search);
  if (query.categoryId !== undefined) params.append("categoryId", String(query.categoryId));
  if (query.status !== undefined) params.append("status", String(query.status));
  if (query.condition !== undefined) params.append("condition", String(query.condition));
  if (query.sortBy) params.append("sortBy", query.sortBy);
  if (query.sortDescending) params.append("sortDescending", "true");
  if (query.page) params.append("page", String(query.page));
  if (query.pageSize) params.append("pageSize", String(query.pageSize));

  const response = await request.get<Result<AssetListView>>(`Asset/GetAssets?${params.toString()}`);
  return response.data;
}

export async function getAsset(id: number): Promise<Result<AssetView>> {
  const response = await request.get<Result<AssetView>>(`Asset/GetAsset/${id}`);
  return response.data;
}

export async function createAsset(input: CreateAssetInput): Promise<Result<AssetView>> {
  const response = await request.post<Result<AssetView>>("Asset/CreateAsset", input);
  return response.data;
}

export async function updateAsset(input: UpdateAssetInput): Promise<Result<AssetView>> {
  const response = await request.put<Result<AssetView>>("Asset/UpdateAsset", input);
  return response.data;
}

export async function archiveAsset(id: number): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>(`Asset/ArchiveAsset/${id}`);
  return response.data;
}

export async function getAssetStats(): Promise<Result<AssetStatsView>> {
  const response = await request.get<Result<AssetStatsView>>("Asset/GetAssetStats");
  return response.data;
}

export async function getEquipmentCoverage(): Promise<Result<EquipmentCoverageView>> {
  const response = await request.get<Result<EquipmentCoverageView>>("Asset/GetEquipmentCoverage");
  return response.data;
}

// Bulk CSV Import
export interface CsvRowValidation {
  row: number;
  field: string;
  message: string;
  severity: "error" | "warning";
}

export interface CsvValidationResultView {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  errors: CsvRowValidation[];
  warnings: CsvRowValidation[];
}

export interface BulkImportResultView {
  importedCount: number;
  skippedCount: number;
  errors: CsvRowValidation[];
}

export async function validateCsvImport(file: File): Promise<Result<CsvValidationResultView>> {
  const formData = new FormData();
  formData.append("file", file);
  const response = await request.post<Result<CsvValidationResultView>>(
    "Asset/ValidateCsvImport",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
}

export async function importCsvAssets(file: File): Promise<Result<BulkImportResultView>> {
  const formData = new FormData();
  formData.append("file", file);
  const response = await request.post<Result<BulkImportResultView>>(
    "Asset/ImportCsvAssets",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
}

// Issuance & Return
export enum AssetIssuanceStatus {
  Active = 'Active',
  Returned = 'Returned',
}

export enum ReturnReason {
  EmployeeTermination = 'EmployeeTermination',
  AssetReplacement = 'AssetReplacement',
  TemporaryAssignmentEnded = 'TemporaryAssignmentEnded',
  AssetNoLongerNeeded = 'AssetNoLongerNeeded',
  RepairMaintenanceRequired = 'RepairMaintenanceRequired',
  Other = 'Other',
}

export interface AssetIssuanceView {
  id: number;
  assetId: number;
  assetName?: string;
  assetRequestId?: number;
  issuedToUserId: string;
  issuedToUserName?: string;
  issueDate: string;
  expectedReturnDate?: string;
  purpose: string;
  conditionAtIssue: AssetCondition;
  issueNotes?: string;
  status: AssetIssuanceStatus;
  returnDate?: string;
  conditionAtReturn?: AssetCondition;
  returnReasonType?: ReturnReason;
  hasDamage: boolean;
  damageDescription?: string;
  damagePhotoUrls: string[];
  repairRequired: boolean;
  repairCost?: number;
  receivedBy?: string;
  digitalSignature?: string;
  returnNotes?: string;
  createdOn: string;
}

export interface IssueAssetInput {
  assetId: number;
  issuedToUserId: string;
  issueDate: string;
  expectedReturnDate?: string;
  purpose: string;
  condition: AssetCondition;
  notes?: string;
  otp: string;
}

export interface ReturnAssetInput {
  assetId: number;
  returnDate: string;
  condition: AssetCondition;
  returnReason: ReturnReason;
  hasDamage: boolean;
  damageDescription?: string;
  repairRequired: boolean;
  repairCost?: number;
  receivedBy: string;
  digitalSignature: string;
  notes?: string;
}

export interface EmployeeSimpleView {
  id: string;
  fullName: string;
  department?: string;
}

export interface AssetOtpView {
  assetId: number;
  sent: boolean;
}

export async function generateOtp(assetId: number, employeeId: string): Promise<Result<AssetOtpView>> {
  const response = await request.post<Result<AssetOtpView>>(
    `Asset/GenerateOtp?assetId=${assetId}&employeeId=${encodeURIComponent(employeeId)}`
  );
  return response.data;
}

export async function issueAsset(input: IssueAssetInput): Promise<Result<AssetIssuanceView>> {
  const response = await request.post<Result<AssetIssuanceView>>("Asset/IssueAsset", input);
  return response.data;
}

export async function returnAsset(input: ReturnAssetInput): Promise<Result<AssetIssuanceView>> {
  const response = await request.post<Result<AssetIssuanceView>>("Asset/ReturnAsset", input);
  return response.data;
}

export async function getAssetHistory(assetId: number): Promise<Result<AssetIssuanceView[]>> {
  const response = await request.get<Result<AssetIssuanceView[]>>(`Asset/GetAssetHistory/${assetId}`);
  return response.data;
}

export async function searchEmployees(search?: string): Promise<Result<EmployeeSimpleView[]>> {
  const params = search ? `?search=${encodeURIComponent(search)}` : "";
  const response = await request.get<Result<EmployeeSimpleView[]>>(`Asset/SearchEmployees${params}`);
  return response.data;
}

// Maintenance
export enum MaintenanceType {
  Cleaning = 'Cleaning',
  Repair = 'Repair',
  Replacement = 'Replacement',
  Inspection = 'Inspection',
  Upgrade = 'Upgrade',
  Calibration = 'Calibration',
  PreventiveMaintenance = 'PreventiveMaintenance',
  Other = 'Other',
}

export interface AssetMaintenanceView {
  id: number;
  assetId: number;
  type: MaintenanceType;
  maintenanceDate: string;
  cost: number;
  vendor?: string;
  performedBy?: string;
  notes?: string;
  createdOn: string;
}

export interface CreateMaintenanceInput {
  assetId: number;
  type: MaintenanceType;
  maintenanceDate: string;
  cost: number;
  vendor?: string;
  performedBy?: string;
  notes?: string;
}

export interface DepreciationYearView {
  year: number;
  yearNumber: number;
  originalValue: number;
  depreciationExpense: number;
  accumulatedDepreciation: number;
  bookValue: number;
  isCurrent: boolean;
}

export interface DepreciationScheduleView {
  originalValue: number;
  currentBookValue: number;
  totalDepreciation: number;
  usefulLifeYears: number;
  currency?: string;
  schedule: DepreciationYearView[];
}

export async function getMaintenanceHistory(assetId: number): Promise<Result<AssetMaintenanceView[]>> {
  const response = await request.get<Result<AssetMaintenanceView[]>>(`Asset/GetMaintenanceHistory/${assetId}`);
  return response.data;
}

export async function createMaintenance(input: CreateMaintenanceInput): Promise<Result<AssetMaintenanceView>> {
  const response = await request.post<Result<AssetMaintenanceView>>("Asset/CreateMaintenance", input);
  return response.data;
}

export async function getDepreciationSchedule(assetId: number, usefulLifeYears: number = 5): Promise<Result<DepreciationScheduleView>> {
  const params = new URLSearchParams();
  params.append("assetId", String(assetId));
  params.append("usefulLifeYears", String(usefulLifeYears));
  const response = await request.get<Result<DepreciationScheduleView>>(`Asset/GetDepreciationSchedule?${params.toString()}`);
  return response.data;
}

// Categories
export async function getCategories(): Promise<Result<AssetCategoryView[]>> {
  const response = await request.get<Result<AssetCategoryView[]>>("Asset/GetCategories");
  return response.data;
}

export async function createCategory(input: CreateAssetCategoryInput): Promise<Result<AssetCategoryView>> {
  const response = await request.post<Result<AssetCategoryView>>("Asset/CreateCategory", input);
  return response.data;
}

export async function updateCategory(input: UpdateAssetCategoryInput): Promise<Result<AssetCategoryView>> {
  const response = await request.put<Result<AssetCategoryView>>("Asset/UpdateCategory", input);
  return response.data;
}

export async function archiveCategory(id: number): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>(`Asset/ArchiveCategory/${id}`);
  return response.data;
}

// ─── Asset Requests ───

export enum AssetRequestStatus {
  Pending = 'Pending',
  Approved = 'Approved',
  Rejected = 'Rejected',
  Fulfilled = 'Fulfilled',
}

export enum AssetRequestPriority {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
}

export interface AssetRequestView {
  id: number;
  categoryId: number;
  categoryName?: string;
  employeeId: string;
  employeeName?: string;
  department?: string;
  reason: string;
  priority: AssetRequestPriority;
  status: AssetRequestStatus;
  reviewedById?: string;
  reviewedByName?: string;
  reviewComment?: string;
  reviewedDate?: string;
  fulfilledByIssuanceId?: number;
  createdOn: string;
}

export interface AssetRequestListView {
  items: AssetRequestView[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CreateAssetRequestInput {
  categoryId: number;
  reason: string;
  priority: AssetRequestPriority;
}

export interface ReviewAssetRequestInput {
  requestId: number;
  status: AssetRequestStatus;
  comment?: string;
}

export interface FulfillAssetRequestInput {
  requestId: number;
  assetId: number;
  condition: AssetCondition;
  notes?: string;
  otp: string;
}

export interface AssetRequestListQuery {
  search?: string;
  status?: AssetRequestStatus;
  priority?: AssetRequestPriority;
  page?: number;
  pageSize?: number;
}

export interface MyAssetsView {
  current: MyAssetItem[];
  history: MyAssetItem[];
}

export interface MyAssetItem {
  assetId: number;
  assetName: string;
  categoryName?: string;
  serialNumber?: string;
  condition: AssetCondition;
  assetStatus: AssetStatus;
  issueDate: string;
  expectedReturnDate?: string;
  returnDate?: string;
  purpose?: string;
}

export async function createAssetRequest(input: CreateAssetRequestInput): Promise<Result<AssetRequestView>> {
  const response = await request.post<Result<AssetRequestView>>("Asset/CreateAssetRequest", input);
  return response.data;
}

export async function getMyAssetRequests(): Promise<Result<AssetRequestView[]>> {
  const response = await request.get<Result<AssetRequestView[]>>("Asset/GetMyAssetRequests");
  return response.data;
}

export async function getAssetRequests(query: AssetRequestListQuery): Promise<Result<AssetRequestListView>> {
  const params = new URLSearchParams();
  if (query.search) params.append("search", query.search);
  if (query.status !== undefined) params.append("status", String(query.status));
  if (query.priority !== undefined) params.append("priority", String(query.priority));
  if (query.page) params.append("page", String(query.page));
  if (query.pageSize) params.append("pageSize", String(query.pageSize));

  const response = await request.get<Result<AssetRequestListView>>(`Asset/GetAssetRequests?${params.toString()}`);
  return response.data;
}

export async function reviewAssetRequest(input: ReviewAssetRequestInput): Promise<Result<AssetRequestView>> {
  const response = await request.post<Result<AssetRequestView>>("Asset/ReviewAssetRequest", input);
  return response.data;
}

export async function getMyAssets(): Promise<Result<MyAssetsView>> {
  const response = await request.get<Result<MyAssetsView>>("Asset/GetMyAssets");
  return response.data;
}

export async function getEmployeeAssets(employeeId: string): Promise<Result<MyAssetsView>> {
  const response = await request.get<Result<MyAssetsView>>(`Asset/GetEmployeeAssets/${employeeId}`);
  return response.data;
}

export async function fulfillAssetRequest(input: FulfillAssetRequestInput): Promise<Result<AssetIssuanceView>> {
  const response = await request.post<Result<AssetIssuanceView>>('Asset/FulfillAssetRequest', input);
  return response.data;
}

export async function getUnfulfilledRequestCount(): Promise<Result<number>> {
  const response = await request.get<Result<number>>('Asset/GetUnfulfilledRequestCount');
  return response.data;
}

// ─── Asset Settings ───

export interface AssetSettingsView {
  requireAssetIssuanceOtp: boolean;
}

export async function getAssetSettings(): Promise<Result<AssetSettingsView>> {
  const response = await request.get<Result<AssetSettingsView>>("Asset/GetAssetSettings");
  return response.data;
}

export async function updateAssetSettings(input: AssetSettingsView): Promise<Result<AssetSettingsView>> {
  const response = await request.post<Result<AssetSettingsView>>("Asset/UpdateAssetSettings", input);
  return response.data;
}

export async function getCategoriesForRequest(): Promise<Result<AssetCategoryView[]>> {
  const response = await request.get<Result<AssetCategoryView[]>>("Asset/GetAssetCategoriesForRequest");
  return response.data;
}
